import React, { useState } from "react";
import { addToast, Button, Spinner } from "@heroui/react";
import { Camera, CameraResultType } from "@capacitor/camera";
import { CameraIcon, ImageIcon, TrashIcon } from "@phosphor-icons/react";

import b64toBlob from "@/shared/utils/b64toBlob";
import { ICON_SIZE } from "@/config/constants";
import { ManageViewImage } from "./ManageViewImage";
import { ManageImageCrop } from "./ManageImageCrop";
import { useOpenState } from "@/shared/hooks/useOpenState";

export interface AvatarPickerProps extends Omit<React.HTMLProps<HTMLDivElement>, "placeholder"> {
  isLoading?: boolean;
  img?: string;
  placeholder?: React.ReactNode;
  onDeleteImage: () => Promise<void> | void;
  onSelectImage: (image: Blob) => Promise<void> | void;
}

const ALLOWED_FILE_TYPES = ["jpeg", "jpg", "png", "bpm", "gif", "webp"];

export function AvatarPicker(props: AvatarPickerProps) {
  const { img, isLoading: externalIsLoading, placeholder, onDeleteImage, onSelectImage, ...rest } = props;
  const [isLoading, setIsLoading] = useState(false);
  const {
    id: viewImageId,
    isOpen: isViewImageOpen,
    handleOpenChange: handleViewImageOpenChange,
  } = useOpenState({ id: "view-image" });
  const {
    id: cropModalId,
    isOpen: isCropModalOpen,
    handleOpenChange: handleCropModalOpenChange,
  } = useOpenState({ id: "crop-modal" });
  const [imgSrc, setImgSrc] = useState<string | ArrayBuffer | null>(null);
  const handleOpenPhoto = async () => {
    const image = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
    });

    if (!image.base64String) {
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(image.format)) {
      addToast({
        title: "Unsupported file type",
        description: "Selected file is not supported.",
        color: "danger",
      });
      return;
    }

    handleCropModalOpenChange(true);

    const reader = new FileReader();
    reader.addEventListener("load", () => setImgSrc(reader.result));
    reader.readAsDataURL(b64toBlob(image.base64String));
  };

  const handleCrop = async (image: Blob | null) => {
    handleCropModalOpenChange(false);

    if (image) {
      setIsLoading(true);
      await onSelectImage(image);
    }

    setIsLoading(false);
  };

  const handleDeleteImage = async () => {
    setIsLoading(true);
    await onDeleteImage();
    setIsLoading(false);
  };

  return (
    <div {...rest}>
      <div className="border-default-400 relative inline-block size-28 rounded-full border-1 border-dashed p-2">
        {isLoading || externalIsLoading ? (
          <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        ) : img ? (
          <Button
            isIconOnly
            isLoading={isLoading}
            isDisabled={!img}
            className="h-full min-h-min w-full min-w-min rounded-full p-0 hover:scale-105"
            variant="light"
            onPress={() => handleViewImageOpenChange(true)}
          >
            <img
              className="w-full overflow-hidden rounded-full transition-opacity duration-300 hover:opacity-90"
              src={img}
              alt=""
            />
          </Button>
        ) : (
          <div className="bg-content1 text-default-500 flex h-full w-full flex-col items-center justify-center rounded-full transition-all duration-150">
            {placeholder ?? <ImageIcon className="text-4xl" />}
          </div>
        )}
        {img && (
          <Button
            isIconOnly
            className="absolute bottom-0 left-0 rounded-full p-2 transition-all duration-100 hover:scale-110"
            size="sm"
            color="danger"
            onPress={handleDeleteImage}
            aria-label="Delete image"
          >
            <TrashIcon size={ICON_SIZE.md} />
          </Button>
        )}
        <Button
          isIconOnly
          isDisabled={isLoading}
          className="absolute right-0 bottom-0 rounded-full p-2 transition-all duration-100 hover:scale-110"
          size="sm"
          color="primary"
          onPress={() => handleOpenPhoto()}
          aria-label="Open camera"
        >
          <CameraIcon size={ICON_SIZE.md} />
        </Button>
      </div>
      <ManageViewImage
        externalState={{ isOpen: isViewImageOpen, onOpenChange: handleViewImageOpenChange }}
        id={cropModalId}
        img={img}
      />
      <ManageImageCrop
        externalState={{ isOpen: isCropModalOpen, onOpenChange: handleCropModalOpenChange }}
        id={viewImageId}
        imgSrc={imgSrc}
        onCrop={handleCrop}
      />
    </div>
  );
}
